import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';
import {
  Clock,
  LayoutDashboard,
  FolderKanban,
  BarChart3,
  Users,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  LogOut,
  Building2,
  Menu,
  X,
  Keyboard,
  Contact,
} from 'lucide-react';
import KeyboardShortcutsGuide from './KeyboardShortcutsGuide';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

const NavItem = ({ to, icon: Icon, children, collapsed }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `sidebar-nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
    }
  >
    <Icon className="w-5 h-5 flex-shrink-0" />
    {!collapsed && <span>{children}</span>}
  </NavLink>
);

const Sidebar = ({
  user,
  organizations,
  currentOrg,
  setCurrentOrg,
  projects,
  isOrgDialogOpen,
  setIsOrgDialogOpen,
  orgName,
  setOrgName,
  orgDesc,
  setOrgDesc,
  handleCreateOrg,
  handleLogout,
  navigate,
  collapsed,
  setCollapsed,
  isMobile,
  openShortcuts,
}) => (
  <aside className={`h-full bg-[#09090B]/90 backdrop-blur-xl border-r border-zinc-800 flex flex-col transition-all duration-300 ${collapsed && !isMobile ? 'w-16' : 'w-64'
    }`}>
    {/* Logo */}
    <div className="p-4 border-b border-zinc-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-black" />
          </div>
          {(!collapsed || isMobile) && (
            <span className="text-lg font-bold text-white font-['Manrope']">Sync Work</span>
          )}
        </div>

        {!isMobile && !collapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 text-zinc-400 hover:text-white"
            data-testid="collapse-sidebar-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
        {!isMobile && collapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 text-zinc-400 hover:text-white"
            data-testid="collapse-sidebar-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>

    {/* Org Switcher */}
    {(!collapsed || isMobile) && (
      <div className="p-4 border-b border-zinc-800">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center justify-between p-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-colors"
              data-testid="org-switcher"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-zinc-400" />
                <span className="text-sm text-white truncate max-w-[140px]">
                  {currentOrg?.name || 'Select Organization'}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-zinc-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-800">
            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => setCurrentOrg(org)}
                className={`cursor-pointer ${currentOrg?.id === org.id ? 'bg-zinc-800 text-cyan-400' : 'text-white'}`}
                data-testid={`org-option-${org.id}`}
              >
                {org.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-zinc-800" />
            <Dialog open={isOrgDialogOpen} onOpenChange={setIsOrgDialogOpen}>
              <DialogTrigger asChild>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer text-cyan-400">
                  <Plus className="w-4 h-4 mr-2" /> New Organization
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Organization</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateOrg} className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Name</label>
                    <Input
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Acme Inc."
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="new-org-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1.5">Description</label>
                    <Textarea
                      value={orgDesc}
                      onChange={(e) => setOrgDesc(e.target.value)}
                      placeholder="Brief description..."
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="new-org-desc-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 text-black hover:bg-cyan-400" data-testid="new-org-submit-btn">
                    Create Organization
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )}

    {/* Navigation */}
    <ScrollArea className="flex-1">
      <TooltipProvider delayDuration={0}>
        <nav className="p-4 space-y-1">
          {collapsed && !isMobile ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink to="/dashboard" className={({ isActive }) => `sidebar-nav-item justify-center px-2 ${isActive ? 'active' : ''}`}>
                    <LayoutDashboard className="w-5 h-5" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">Dashboard</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink to="/reports" className={({ isActive }) => `sidebar-nav-item justify-center px-2 ${isActive ? 'active' : ''}`}>
                    <BarChart3 className="w-5 h-5" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">Reports</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink to="/people" className={({ isActive }) => `sidebar-nav-item justify-center px-2 ${isActive ? 'active' : ''}`}>
                    <Users className="w-5 h-5" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">People</TooltipContent>
              </Tooltip>
              {(currentOrg?.user_role === 'admin' || currentOrg?.user_role === 'project_manager') && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <NavLink to="/person-report" className={({ isActive }) => `sidebar-nav-item justify-center px-2 ${isActive ? 'active' : ''}`}>
                      <Contact className="w-5 h-5" />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">Person's Report</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink to="/settings" className={({ isActive }) => `sidebar-nav-item justify-center px-2 ${isActive ? 'active' : ''}`}>
                    <Settings className="w-5 h-5" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">Settings</TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <NavItem to="/dashboard" icon={LayoutDashboard} collapsed={false}>Dashboard</NavItem>
              <NavItem to="/reports" icon={BarChart3} collapsed={false}>Reports</NavItem>
              <NavItem to="/people" icon={Users} collapsed={false}>People</NavItem>
              {(currentOrg?.user_role === 'admin' || currentOrg?.user_role === 'project_manager') && (
                <NavItem to="/person-report" icon={Contact} collapsed={false}>Person's Report</NavItem>
              )}
              <NavItem to="/settings" icon={Settings} collapsed={false}>Settings</NavItem>
            </>
          )}
        </nav>

        {/* Projects List */}
        {currentOrg && projects.length > 0 && (!collapsed || isMobile) && (
          <div className="px-4 py-2">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2 px-3">Projects</h3>
            <div className="space-y-1">
              {projects.slice(0, 10).map((project) => (
                <NavLink
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                  data-testid={`sidebar-project-${project.id}`}
                >
                  <FolderKanban className="w-5 h-5" />
                  <span className="truncate">{project.name}</span>
                </NavLink>
              ))}
            </div>
          </div>
        )}

        {/* Collapsed projects */}
        {currentOrg && projects.length > 0 && collapsed && !isMobile && (
          <div className="px-2 py-2">
            {projects.slice(0, 5).map((project) => (
              <Tooltip key={project.id}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={`/projects/${project.id}`}
                    className={({ isActive }) => `sidebar-nav-item justify-center px-2 ${isActive ? 'active' : ''}`}
                    data-testid={`sidebar-project-${project.id}`}
                  >
                    <FolderKanban className="w-5 h-5" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">{project.name}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        )}
      </TooltipProvider>
    </ScrollArea>

    {/* User Menu */}
    <div className="p-4 border-t border-zinc-800">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 transition-colors ${collapsed && !isMobile ? 'justify-center' : ''
            }`} data-testid="user-menu">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-violet-500 text-sm font-medium text-white">
                {user?.name?.charAt(0).toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            {(!collapsed || isMobile) && (
              <>
                <div className="flex-1 text-left">
                  <p className="text-sm text-white truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-400" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-800" align="end">
          <DropdownMenuItem onClick={openShortcuts} className="cursor-pointer text-white">
            <Keyboard className="w-4 h-4 mr-2" /> Keyboard Shortcuts
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer text-white">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-zinc-800" />
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400" data-testid="logout-menu-item">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </aside>
);

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { organizations, currentOrg, setCurrentOrg, projects, fetchProjects, createOrganization, sidebarCollapsed, setSidebarCollapsed } = useAppStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgDesc, setOrgDesc] = useState('');
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);

  useEffect(() => {
    if (currentOrg) {
      fetchProjects(currentOrg.id);
    }
  }, [currentOrg, fetchProjects]);

  const handleCreateOrg = useCallback(async (e) => {
    e.preventDefault();
    try {
      await createOrganization(orgName, orgDesc);
      toast.success('Organization created!');
      setIsOrgDialogOpen(false);
      setOrgName('');
      setOrgDesc('');
    } catch (err) {
      toast.error(err.message);
    }
  }, [createOrganization, orgName, orgDesc]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
    toast.success('Logged out');
  }, [logout, navigate]);

  const sidebarProps = {
    user,
    organizations,
    currentOrg,
    setCurrentOrg,
    projects,
    isOrgDialogOpen,
    setIsOrgDialogOpen,
    orgName,
    setOrgName,
    orgDesc,
    setOrgDesc,
    handleCreateOrg,
    handleLogout,
    navigate,
    collapsed: sidebarCollapsed,
    setCollapsed: setSidebarCollapsed,
    isMobile: false,
    openShortcuts: () => setIsShortcutsOpen(true),
  };

  return (
    <div className="h-screen flex bg-[#09090B]">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar {...sidebarProps} />
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#09090B]/90 backdrop-blur-xl border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-black" />
            </div>
            <span className="text-lg font-bold text-white">Sync Work</span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-zinc-400 hover:text-white"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="w-64 h-full" onClick={(e) => e.stopPropagation()}>
            <Sidebar {...sidebarProps} isMobile={true} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto md:pt-0 pt-16">
        {children}
      </main>
      <KeyboardShortcutsGuide open={isShortcutsOpen} onOpenChange={setIsShortcutsOpen} />
    </div>
  );
}
